import { db, type LiftXDatabase } from '../db';
import type { BackupRecordRow } from '../types';

/**
 * The only code that reads or writes `backupRecords`. Master Phase 9.
 *
 * Every backup write — manual export, a scheduled automatic backup, or a
 * `preRestore` safety copy — goes through `record()`, so "when did I last
 * back up" (Phase 0 §11.3) and grandfather-father-son pruning both read from
 * one table with one shape, never a second bookkeeping mechanism.
 */

export interface BackupRecordsRepository {
  record(input: Omit<BackupRecordRow, 'id'>): Promise<BackupRecordRow>;
  /** Most recent row of one kind, or undefined if none exists yet. */
  latestOf(kind: string): Promise<BackupRecordRow | undefined>;
  /** Most-recent-first, across every kind. */
  listRecent(limit?: number): Promise<BackupRecordRow[]>;
  /**
   * Keeps the `keep` most-recent rows of `kind` by `createdAt`, deletes the
   * rest. Never called for `'manual'`/`'preRestore'` — omission, not a
   * conditional inside this function, is what keeps them un-prunable.
   */
  pruneKind(kind: string, keep: number): Promise<number>;
}

export function createBackupRecordsRepository(database: LiftXDatabase = db): BackupRecordsRepository {
  return {
    async record(input): Promise<BackupRecordRow> {
      const row: BackupRecordRow = { id: crypto.randomUUID(), ...input };
      await database.backupRecords.add(row);
      return row;
    },

    async latestOf(kind): Promise<BackupRecordRow | undefined> {
      const rows = await database.backupRecords.where('kind').equals(kind).toArray();
      return rows.sort((a, b) => b.createdAt - a.createdAt)[0];
    },

    async listRecent(limit = 20): Promise<BackupRecordRow[]> {
      const rows = await database.backupRecords.toArray();
      return rows.sort((a, b) => b.createdAt - a.createdAt).slice(0, limit);
    },

    async pruneKind(kind, keep): Promise<number> {
      const rows = await database.backupRecords.where('kind').equals(kind).toArray();
      const sorted = rows.sort((a, b) => b.createdAt - a.createdAt);
      const toDelete = sorted.slice(keep);
      if (toDelete.length > 0) {
        await database.backupRecords.bulkDelete(toDelete.map((row) => row.id));
      }
      return toDelete.length;
    },
  };
}

export const backupRecordsRepository = createBackupRecordsRepository();
